import { useState, useEffect, useRef, useMemo } from 'react';
import api, { getContracts, createContract, updateContract, deleteContract, getClients, getExpiringContracts, getCustomFields, getProducts, getContractProducts, getContractProductsBulk, addContractProduct, updateContractProduct, removeContractProduct, getContractStatus, getExpiringProducts, getExpiredProducts, getBusinessUnits, getUsers, updatePMSnapshot, getContractAttachments, uploadContractAttachment, downloadContractAttachment, deleteContractAttachment, getContractsExportFields, exportContractsCsv } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { MultiSelect } from '../components/ui/multi-select';
import { Combobox } from '../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, MoreHorizontal, Pencil, Trash2, FileText, Search, Loader2, AlertTriangle, Calendar, Package, X, Filter, Clock, Users, Upload, Download, Paperclip, File, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { format, differenceInDays, parseISO, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters, startOfYear, endOfYear, subYears } from 'date-fns';
import { it } from 'date-fns/locale';
import PeriodPresets, { PERIOD_PRESETS } from '../components/PeriodPresets';

const formatCurrency = (value) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

// Date preset options
const datePresets = [
  { label: 'Questo mese', getValue: () => ({ from: format(startOfMonth(new Date()), 'yyyy-MM-dd'), to: format(endOfMonth(new Date()), 'yyyy-MM-dd') }) },
  { label: 'Mese scorso', getValue: () => ({ from: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'), to: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd') }) },
  { label: 'Questo trimestre', getValue: () => ({ from: format(startOfQuarter(new Date()), 'yyyy-MM-dd'), to: format(endOfQuarter(new Date()), 'yyyy-MM-dd') }) },
  { label: 'Trimestre scorso', getValue: () => ({ from: format(startOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd'), to: format(endOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd') }) },
  { label: 'Quest\'anno', getValue: () => ({ from: format(startOfYear(new Date()), 'yyyy-MM-dd'), to: format(endOfYear(new Date()), 'yyyy-MM-dd') }) },
  { label: 'Anno scorso', getValue: () => ({ from: format(startOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd'), to: format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd') }) },
];

export default function ContractsPage() {
  const { hasRole } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [expiredContracts, setExpiredContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [products, setProducts] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [pmUsers, setPmUsers] = useState([]);
  const [accountUsers, setAccountUsers] = useState([]);
  const [contractProductsMap, setContractProductsMap] = useState({}); // contract_id -> products with details
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState(['open']); // Default: mostra solo contratti aperti (array per multi-select)
  
  // Nuovi filtri per la tab principale "Tutti"
  const [filterBUs, setFilterBUs] = useState([]); // Business Unit - multi-select
  const [filterProducts, setFilterProducts] = useState([]); // Prodotti - multi-select
  const [filterAccounts, setFilterAccounts] = useState([]); // Account Manager - multi-select
  const [filterSettori, setFilterSettori] = useState([]); // Settore cliente - multi-select

  // Settori disponibili (sincronizzati con ClientsPage)
  const SETTORI = [
    'Agricoltura e Agroalimentare', 'Industria e Produzione', 'Edilizia e Immobiliare',
    'Energia e Ambiente', 'Trasporti e Logistica', 'Commercio (Retail & Wholesale)',
    'Tecnologia e IT', 'Marketing e Comunicazione', 'Consulenza e Servizi B2B',
    'Finanza e Assicurazioni', 'Sanità e Benessere', 'Formazione ed Educazione',
    'Turismo e Hospitality', 'Ristorazione e Food Service', 'Media e Intrattenimento',
    'Sport e Fitness', 'Moda e Lusso', 'Automotive', 'Pubblica Amministrazione',
    'No Profit e Associazioni', 'Altro'
  ];
  
  // Filtro data firma (signing_date) per la tab "Tutti"
  const [signingDatePreset, setSigningDatePreset] = useState('all');
  const [signingDateFrom, setSigningDateFrom] = useState('');
  const [signingDateTo, setSigningDateTo] = useState('');
  
  // Filtri per campi personalizzati del contratto
  const [cfFilters, setCfFilters] = useState({});

  // ─── Export CSV state ────────────────────────────────────────
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFields, setExportFields] = useState({ contract_std: [], contract_custom: [], client_std: [], client_custom: [] });
  const DEFAULT_EXPORT_KEYS = [
    'contract.name', 'contract.client_name', 'contract.business_units',
    'contract.signing_date', 'contract.value', 'contract.products_names',
    'contract.account_manager', 'contract.status',
  ];
  const [selectedExportKeys, setSelectedExportKeys] = useState(() => {
    try {
      const saved = localStorage.getItem('contracts_export_selected_keys_v2');
      return saved ? JSON.parse(saved) : DEFAULT_EXPORT_KEYS;
    } catch { return DEFAULT_EXPORT_KEYS; }
  });
  const [exporting, setExporting] = useState(false);

  // Colonne configurabili - persistenza localStorage
  const defaultCols = { name: true, client: true, products: true, value: true, signing_date: true, account_manager: true };
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const s = localStorage.getItem('contracts_columns_all');
      if (!s) return defaultCols;
      const parsed = JSON.parse(s);
      // Ensure newly-introduced default columns appear for users with saved settings
      return { ...defaultCols, ...parsed, account_manager: parsed.account_manager ?? true };
    } catch { return defaultCols; }
  });
  const [colMenuOpen, setColMenuOpen] = useState(false);
  // Colonne per la tab In Scadenza
  const defaultExpCols = { product: true, contract: true, project: true, client: true, pm: true, expiry_date: true, status: true, outcome_date: true, value: true, renewal_value: true, account_manager: true };
  const [visibleExpColumns, setVisibleExpColumns] = useState(() => {
    try {
      const s = localStorage.getItem('contracts_columns_exp');
      if (!s) return defaultExpCols;
      const parsed = JSON.parse(s);
      return { ...defaultExpCols, ...parsed, account_manager: parsed.account_manager ?? true, renewal_value: parsed.renewal_value ?? true };
    } catch { return defaultExpCols; }
  });
  const [expColMenuOpen, setExpColMenuOpen] = useState(false);

  // Get contract custom fields (type select)
  const contractCFs = customFields.filter(f => f.entity_type === 'contract' && (f.field_type === 'select' || f.field_type === 'multiselect'));
  // All contract custom fields for columns
  const allContractCFs = customFields.filter(f => f.entity_type === 'contract');

  const toggleColumn = (key) => {
    const next = { ...visibleColumns, [key]: !visibleColumns[key] };
    setVisibleColumns(next);
    localStorage.setItem('contracts_columns_all', JSON.stringify(next));
  };
  const toggleExpColumn = (key) => {
    const next = { ...visibleExpColumns, [key]: !visibleExpColumns[key] };
    setVisibleExpColumns(next);
    localStorage.setItem('contracts_columns_exp', JSON.stringify(next));
  };

  // Selezione multipla contratti
  const [selectedContracts, setSelectedContracts] = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkCfValues, setBulkCfValues] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  const toggleSelectContract = (id) => {
    setSelectedContracts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (contractsList) => {
    if (selectedContracts.size === contractsList.length) {
      setSelectedContracts(new Set());
    } else {
      setSelectedContracts(new Set(contractsList.map(c => c.id)));
    }
  };
  const selectedTotal = useMemo(() => {
    return Array.from(selectedContracts).reduce((sum, id) => {
      const cps = contractProductsMap[id] || [];
      const val = cps.reduce((s, cp) => s + (cp.value || 0), 0);
      return sum + val;
    }, 0);
  }, [selectedContracts, contractProductsMap]);

  const handleBulkEdit = async () => {
    setBulkSaving(true);
    try {
      const updates = Object.entries(bulkCfValues).filter(([, v]) => v !== undefined && v !== '');
      if (updates.length === 0) {
        toast({ title: 'Nessuna modifica', description: 'Seleziona almeno un campo da modificare', variant: 'destructive' });
        setBulkSaving(false);
        return;
      }
      for (const id of selectedContracts) {
        const contract = contracts.find(c => c.id === id);
        if (!contract) continue;
        const newCf = { ...(contract.custom_fields || {}) };
        for (const [fid, val] of updates) {
          if (val === '__clear__') delete newCf[fid];
          else newCf[fid] = val;
        }
        await updateContract(id, { custom_fields: newCf });
      }
      toast({ title: 'Modifica completata', description: `${selectedContracts.size} contratti aggiornati` });
      setBulkEditOpen(false);
      setBulkCfValues({});
      setSelectedContracts(new Set());
      loadData();
    } catch (e) {
      toast({ title: 'Errore', description: 'Errore durante la modifica massiva', variant: 'destructive' });
    }
    setBulkSaving(false);
  };

  const handleSigningDatePresetChange = (presetValue) => {
    setSigningDatePreset(presetValue);
    if (presetValue === 'all') {
      setSigningDateFrom(''); setSigningDateTo('');
    } else if (presetValue !== 'custom') {
      const preset = PERIOD_PRESETS.find(p => p.value === presetValue);
      if (preset) {
        const { start, end } = preset.getRange();
        setSigningDateFrom(start); setSigningDateTo(end);
      }
    }
  };
  
  // Ordinamento tabella contratti
  const [sortColumn, setSortColumn] = useState('name'); // Colonna di ordinamento
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' o 'desc'
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [selectedContractForProducts, setSelectedContractForProducts] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // Advanced filters for expiring/expired tab
  const [expiryFilters, setExpiryFilters] = useState({
    search: '',
    business_unit_ids: [],  // Array per multi-select
    product_ids: [],        // Array per multi-select
    pm_ids: [],             // Array per multi-select
    account_ids: [],        // Array per multi-select - Account Manager
    outcome_date_from: '',
    outcome_date_to: '',
    datePreset: '',
    expiry_date_from: '',
    expiry_date_to: '',
    expiryDatePreset: '',
  });

  // PM Snapshot edit dialog
  const [pmSnapshotDialog, setPmSnapshotDialog] = useState({
    open: false,
    contractId: null,
    contractProductId: null,
    currentPmIds: [],
    productName: '',
  });

  // Edit Outcome Date Dialog state
  const [editOutcomeDateDialog, setEditOutcomeDateDialog] = useState({
    open: false, contractId: null, contractProductId: null, outcomeDate: '',
  });

  const statusOptions = [
    { value: 'open', label: 'Aperto', color: 'bg-blue-100 text-blue-700' },
    { value: 'lost', label: 'Perso', color: 'bg-red-100 text-red-700' },
    { value: 'renewed', label: 'Rinnovato', color: 'bg-green-100 text-green-700' },
    { value: 'withdrawn', label: 'Recesso', color: 'bg-purple-100 text-purple-700' },
  ];

  // State for status change dialog
  const [statusChangeDialog, setStatusChangeDialog] = useState({
    open: false,
    contractId: null,
    contractProductId: null,
    newStatus: null,
    outcomeDate: '',
    renewalContractId: '',
    renewalContractProductIds: [],
  });
  const [renewalSearchTerm, setRenewalSearchTerm] = useState('');
  const [renewalContractProducts, setRenewalContractProducts] = useState([]);
  const [loadingRenewalProducts, setLoadingRenewalProducts] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    signing_date: '',
    notes: '',
    custom_fields: {},
  });

  // Product form for adding/editing in contract
  const [productFormData, setProductFormData] = useState({
    product_id: '',
    value: '',
    start_date: '',
    end_date: '',
  });
  const [editingContractProduct, setEditingContractProduct] = useState(null);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    // Debounce: evita chiamate multiple durante digitazione veloce nel search/filtri
    const t = setTimeout(() => { loadData(); }, 350);
    return () => clearTimeout(t);
  }, [filterStatus, expiryFilters]);

  useEffect(() => {
    loadFilterData();
  }, []);

  const loadFilterData = async () => {
    try {
      const [buRes, pmRes, accountRes] = await Promise.all([
        getBusinessUnits(),
        getUsers('project_manager'),
        getUsers('account'),
      ]);
      setBusinessUnits(buRes.data);
      setPmUsers(pmRes.data);
      setAccountUsers(accountRes.data);
    } catch (err) {
      console.error('Error loading filter data:', err);
    }
  };

  const loadData = async () => {
    try {
      // Build filter params for expiring/expired (arrays joined by comma for API)
      const expiryParams = {
        status: filterStatus.length > 0 ? filterStatus.join(',') : undefined,
        search: expiryFilters.search || undefined,
        business_unit_id: expiryFilters.business_unit_ids.length > 0 ? expiryFilters.business_unit_ids.join(',') : undefined,
        product_id: expiryFilters.product_ids.length > 0 ? expiryFilters.product_ids.join(',') : undefined,
        pm_id: expiryFilters.pm_ids.length > 0 ? expiryFilters.pm_ids.join(',') : undefined,
        account_id: expiryFilters.account_ids.length > 0 ? expiryFilters.account_ids.join(',') : undefined,
        outcome_date_from: expiryFilters.outcome_date_from || undefined,
        outcome_date_to: expiryFilters.outcome_date_to || undefined,
        expiry_date_from: expiryFilters.expiry_date_from || undefined,
        expiry_date_to: expiryFilters.expiry_date_to || undefined,
      };
      
      const [contractsRes, clientsRes, expiringRes, expiredRes, fieldsRes, productsRes] = await Promise.all([
        getContracts(),
        getClients(),
        getExpiringProducts(expiryParams),
        getExpiredProducts(expiryParams),
        getCustomFields('contract'),
        getProducts(),
      ]);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
      setExpiringContracts(expiringRes.data);
      setExpiredContracts(expiredRes.data);
      setCustomFields(fieldsRes.data);
      setProducts(productsRes.data);

      // Load contract products in BULK con CHUNKING: evita URL troppo lunghi
      // (con >100 contratti la query string supera 8KB e nginx la rifiuta silently)
      const contractIds = contractsRes.data.map(c => c.id);
      let cpMap = {};
      if (contractIds.length > 0) {
        try {
          const CHUNK_SIZE = 50;
          for (let i = 0; i < contractIds.length; i += CHUNK_SIZE) {
            const chunk = contractIds.slice(i, i + CHUNK_SIZE);
            const bulkRes = await getContractProductsBulk(chunk);
            Object.assign(cpMap, bulkRes.data || {});
          }
        } catch (bulkErr) {
          console.error('Bulk contract-products fetch failed:', bulkErr);
        }
      }
      setContractProductsMap(cpMap);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare i dati', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (contract = null) => {
    if (contract) {
      setEditingContract(contract);
      setFormData({
        name: contract.name,
        client_id: contract.client_id,
        signing_date: contract.signing_date?.split('T')[0] || '',
        notes: contract.notes || '',
        custom_fields: contract.custom_fields || {},
      });
    } else {
      setEditingContract(null);
      setFormData({
        name: '',
        client_id: '',
        signing_date: '',
        notes: '',
        custom_fields: {},
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required custom fields
    const missingCF = customFields.filter(f => f.is_required && !formData.custom_fields[f.id]);
    if (missingCF.length > 0) {
      toast({ title: 'Campi obbligatori', description: `Compila: ${missingCF.map(f => f.name).join(', ')}`, variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: formData.name,
        client_id: formData.client_id,
        signing_date: formData.signing_date ? new Date(formData.signing_date).toISOString() : null,
        notes: formData.notes || null,
        custom_fields: formData.custom_fields,
      };

      if (editingContract) {
        await updateContract(editingContract.id, data);
        toast({ title: 'Contratto aggiornato' });
      } else {
        await createContract(data);
        toast({ title: 'Contratto creato' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contract) => {
    if (!window.confirm(`Eliminare il contratto "${contract.name}"?`)) return;
    try {
      await deleteContract(contract.id);
      toast({ title: 'Contratto eliminato' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile eliminare il contratto', variant: 'destructive' });
    }
  };

  // Product management functions
  const handleOpenProductDialog = async (contract) => {
    setSelectedContractForProducts(contract);
    setProductFormData({
      product_id: '',
      value: '',
      start_date: contract.signing_date?.split('T')[0] || '',
      end_date: '',
    });
    setProductDialogOpen(true);
    
    // Load attachments for this contract
    await loadAttachments(contract.id);
  };

  // Load attachments for a contract
  const loadAttachments = async (contractId) => {
    setLoadingAttachments(true);
    try {
      const res = await getContractAttachments(contractId);
      setAttachments(res.data);
    } catch (err) {
      console.error('Error loading attachments:', err);
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast({ title: 'Errore', description: 'Sono ammessi solo file PDF', variant: 'destructive' });
      return;
    }

    // Validate file size (25MB max)
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({ title: 'Errore', description: 'Il file è troppo grande. Dimensione massima: 25 MB', variant: 'destructive' });
      return;
    }

    setUploadingAttachment(true);
    try {
      await uploadContractAttachment(selectedContractForProducts.id, file);
      toast({ title: 'Allegato caricato' });
      await loadAttachments(selectedContractForProducts.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile caricare il file', variant: 'destructive' });
    } finally {
      setUploadingAttachment(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle file download
  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await downloadContractAttachment(selectedContractForProducts.id, attachment.id);
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile scaricare il file', variant: 'destructive' });
    }
  };

  // Handle attachment delete
  const handleDeleteAttachment = async (attachment) => {
    if (!window.confirm(`Eliminare l'allegato "${attachment.original_filename}"?`)) return;
    try {
      await deleteContractAttachment(selectedContractForProducts.id, attachment.id);
      toast({ title: 'Allegato eliminato' });
      await loadAttachments(selectedContractForProducts.id);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile eliminare il file', variant: 'destructive' });
    }
  };

  // Check if user can delete attachment
  const canDeleteAttachment = (attachment) => {
    return hasRole(['admin']) || attachment.uploaded_by === user?.id;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleEditContractProduct = (cp) => {
    setEditingContractProduct(cp);
    setProductFormData({
      product_id: cp.product_id,
      value: cp.value?.toString() || '',
      start_date: cp.start_date?.split('T')[0] || '',
      end_date: cp.end_date?.split('T')[0] || '',
    });
  };

  const handleCancelEditProduct = () => {
    setEditingContractProduct(null);
    setProductFormData({
      product_id: '',
      value: '',
      start_date: '',
      end_date: '',
    });
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!productFormData.value) {
      toast({ title: 'Errore', description: 'Inserisci il valore', variant: 'destructive' });
      return;
    }
    
    const product = products.find(p => p.id === (editingContractProduct?.product_id || productFormData.product_id));
    if (product?.has_expiration && (!productFormData.start_date || !productFormData.end_date)) {
      toast({ title: 'Errore', description: 'Inserisci le date di inizio e fine per questo prodotto', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      if (editingContractProduct) {
        // Update existing product
        await updateContractProduct(selectedContractForProducts.id, editingContractProduct.id, {
          value: parseFloat(productFormData.value),
          start_date: productFormData.start_date ? new Date(productFormData.start_date).toISOString() : null,
          end_date: productFormData.end_date ? new Date(productFormData.end_date).toISOString() : null,
        });
        toast({ title: 'Prodotto aggiornato' });
        setEditingContractProduct(null);
      } else {
        // Add new product
        if (!productFormData.product_id) {
          toast({ title: 'Errore', description: 'Seleziona un prodotto', variant: 'destructive' });
          setSaving(false);
          return;
        }
        await addContractProduct(selectedContractForProducts.id, {
          product_id: productFormData.product_id,
          value: parseFloat(productFormData.value),
          start_date: productFormData.start_date ? new Date(productFormData.start_date).toISOString() : null,
          end_date: productFormData.end_date ? new Date(productFormData.end_date).toISOString() : null,
        });
        toast({ title: 'Prodotto aggiunto' });
      }
      setProductFormData({ product_id: '', value: '', start_date: '', end_date: '' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProduct = async (contractId, contractProductId) => {
    if (!window.confirm('Rimuovere questo prodotto dal contratto?')) return;
    try {
      await removeContractProduct(contractId, contractProductId);
      toast({ title: 'Prodotto rimosso' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile rimuovere il prodotto', variant: 'destructive' });
    }
  };

  const handleStatusChangeRequest = (contractId, contractProductId, newStatus) => {
    if (newStatus === 'open') {
      // If changing back to "Aperto", no date needed
      handleConfirmStatusChange(contractId, contractProductId, newStatus, null);
    } else {
      // For other statuses, show dialog to request outcome date (and renewal info)
      setStatusChangeDialog({
        open: true,
        contractId,
        contractProductId,
        newStatus,
        outcomeDate: new Date().toISOString().split('T')[0],
        renewalContractId: '',
        renewalContractProductIds: [],
      });
      setRenewalSearchTerm('');
      setRenewalContractProducts([]);
    }
  };
  
  // Allow editing the renewal reference for items already marked as 'renewed'
  const handleEditRenewal = async (item) => {
    const outcomeISO = item.outcome_date ? new Date(item.outcome_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setStatusChangeDialog({
      open: true,
      contractId: item.contract_id,
      contractProductId: item.contract_product_id,
      newStatus: 'renewed',
      outcomeDate: outcomeISO,
      renewalContractId: item.renewal_contract_id || '',
      renewalContractProductIds: item.renewal_contract_product_ids || [],
    });
    setRenewalSearchTerm('');
    // Preload products of currently linked renewal contract (if any)
    if (item.renewal_contract_id) {
      setLoadingRenewalProducts(true);
      try {
        const res = await api.get(`/contracts/${item.renewal_contract_id}/products`);
        setRenewalContractProducts(res.data || []);
      } catch {
        setRenewalContractProducts([]);
      } finally {
        setLoadingRenewalProducts(false);
      }
    } else {
      setRenewalContractProducts([]);
    }
  };

  // Load products of the chosen renewal contract when user selects one
  const handleSelectRenewalContract = async (contractId) => {
    // Auto-allinea outcome_date alla signing_date del contratto di rinnovo selezionato
    const renewalContract = contracts.find(c => c.id === contractId);
    const autoOutcomeDate = renewalContract?.signing_date
      ? String(renewalContract.signing_date).slice(0, 10)
      : undefined;
    setStatusChangeDialog(prev => ({
      ...prev,
      renewalContractId: contractId,
      renewalContractProductIds: [],
      ...(autoOutcomeDate ? { outcomeDate: autoOutcomeDate } : {}),
    }));
    if (!contractId) {
      setRenewalContractProducts([]);
      return;
    }
    setLoadingRenewalProducts(true);
    try {
      const res = await api.get(`/contracts/${contractId}/products`);
      setRenewalContractProducts(res.data || []);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare i prodotti del contratto', variant: 'destructive' });
      setRenewalContractProducts([]);
    } finally {
      setLoadingRenewalProducts(false);
    }
  };

  const toggleRenewalProduct = (cpId) => {
    setStatusChangeDialog(prev => {
      const ids = new Set(prev.renewalContractProductIds || []);
      if (ids.has(cpId)) ids.delete(cpId); else ids.add(cpId);
      return { ...prev, renewalContractProductIds: Array.from(ids) };
    });
  };

  const handleConfirmStatusChange = async (contractId, contractProductId, status, outcomeDate, renewalContractId, renewalContractProductIds) => {
    try {
      const updateData = { status };
      if (outcomeDate) {
        updateData.outcome_date = new Date(outcomeDate).toISOString();
      }
      if (status === 'renewed' && renewalContractId) {
        updateData.renewal_contract_id = renewalContractId;
        updateData.renewal_contract_product_ids = renewalContractProductIds || [];
      }
      await updateContractProduct(contractId, contractProductId, updateData);
      const statusLabel = statusOptions.find(s => s.value === status)?.label || status;
      toast({ title: `Stato aggiornato a "${statusLabel}"` });
      setStatusChangeDialog({ open: false, contractId: null, contractProductId: null, newStatus: null, outcomeDate: '', renewalContractId: '', renewalContractProductIds: [] });
      setRenewalContractProducts([]);
      setRenewalSearchTerm('');
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile aggiornare lo stato', variant: 'destructive' });
    }
  };

  const handleUpdateProductStatus = async (contractId, contractProductId, status) => {
    handleStatusChangeRequest(contractId, contractProductId, status);
  };

  const handleEditOutcomeDate = (item) => {
    const currentDate = item.outcome_date ? item.outcome_date.split('T')[0] : new Date().toISOString().split('T')[0];
    setEditOutcomeDateDialog({
      open: true,
      contractId: item.contract_id,
      contractProductId: item.contract_product_id,
      outcomeDate: currentDate,
    });
  };

  const handleConfirmEditOutcomeDate = async () => {
    try {
      const { contractId, contractProductId, outcomeDate } = editOutcomeDateDialog;
      await updateContractProduct(contractId, contractProductId, {
        outcome_date: new Date(outcomeDate).toISOString(),
      });
      toast({ title: 'Data esito aggiornata' });
      setEditOutcomeDateDialog({ open: false, contractId: null, contractProductId: null, outcomeDate: '' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile aggiornare la data', variant: 'destructive' });
    }
  };

  // Filter handlers
  const handleExpiryFilterChange = (key, value) => {
    setExpiryFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const handleDatePresetChange = (preset) => {
    if (!preset) {
      setExpiryFilters(prev => ({ ...prev, datePreset: '', outcome_date_from: '', outcome_date_to: '' }));
      return;
    }
    
    // Handle custom date range - show date inputs without pre-filling
    if (preset === 'custom') {
      setExpiryFilters(prev => ({ ...prev, datePreset: 'custom', outcome_date_from: '', outcome_date_to: '' }));
      return;
    }
    
    const presetOption = datePresets.find(p => p.label === preset);
    if (presetOption) {
      const { from, to } = presetOption.getValue();
      setExpiryFilters(prev => ({ ...prev, datePreset: preset, outcome_date_from: from, outcome_date_to: to }));
    }
  };

  const handleExpiryDatePresetChange = (preset) => {
    if (!preset) {
      setExpiryFilters(prev => ({ ...prev, expiryDatePreset: '', expiry_date_from: '', expiry_date_to: '' }));
      return;
    }
    
    // Handle custom date range - show date inputs without pre-filling
    if (preset === 'custom') {
      setExpiryFilters(prev => ({ ...prev, expiryDatePreset: 'custom', expiry_date_from: '', expiry_date_to: '' }));
      return;
    }
    
    const presetOption = datePresets.find(p => p.label === preset);
    if (presetOption) {
      const { from, to } = presetOption.getValue();
      setExpiryFilters(prev => ({ ...prev, expiryDatePreset: preset, expiry_date_from: from, expiry_date_to: to }));
    }
  };

  const resetExpiryFilters = () => {
    setExpiryFilters({
      search: '',
      business_unit_ids: [],
      product_ids: [],
      pm_ids: [],
      account_ids: [],
      outcome_date_from: '',
      outcome_date_to: '',
      datePreset: '',
      expiry_date_from: '',
      expiry_date_to: '',
      expiryDatePreset: '',
    });
    setFilterStatus(['open']); // Reset to default "open"
  };

  // PM Snapshot edit handlers
  const openPmSnapshotDialog = (item) => {
    const currentPmIds = item.pm_snapshot ? item.pm_snapshot.map(pm => pm.id) : [];
    setPmSnapshotDialog({
      open: true,
      contractId: item.contract_id,
      contractProductId: item.contract_product_id,
      currentPmIds,
      productName: item.product_name,
    });
  };

  const handlePmSnapshotSave = async () => {
    try {
      await updatePMSnapshot(pmSnapshotDialog.contractId, pmSnapshotDialog.contractProductId, pmSnapshotDialog.currentPmIds);
      toast({ title: 'Project Manager aggiornati' });
      setPmSnapshotDialog({ open: false, contractId: null, contractProductId: null, currentPmIds: [], productName: '' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile aggiornare i PM', variant: 'destructive' });
    }
  };

  const togglePmInSnapshot = (pmId) => {
    setPmSnapshotDialog(prev => {
      const currentPmIds = prev.currentPmIds.includes(pmId)
        ? prev.currentPmIds.filter(id => id !== pmId)
        : [...prev.currentPmIds, pmId];
      return { ...prev, currentPmIds };
    });
  };

  const getContractTotalValue = (contractId) => {
    const cps = contractProductsMap[contractId] || [];
    if (cps.length === 0) {
      const contract = contracts.find(c => c.id === contractId);
      return contract?.value || 0;
    }
    return cps.reduce((sum, cp) => sum + (cp.value || 0), 0);
  };

  const getContractProductsInfo = (contractId) => {
    const cps = contractProductsMap[contractId] || [];
    if (cps.length === 0) return null;
    
    const now = new Date();
    let expired = 0;
    cps.forEach(cp => {
      if (cp.product?.has_expiration && cp.end_date) {
        const endDate = typeof cp.end_date === 'string' ? parseISO(cp.end_date) : cp.end_date;
        if (endDate < now) expired++;
      }
    });
    
    return { total: cps.length, expired };
  };

  const getClientName = (clientId) => clients.find((c) => c.id === clientId)?.company_name || 'N/A';

  const getExpiryStatus = (endDate) => {
    if (!endDate) return null;
    const days = differenceInDays(parseISO(endDate), new Date());
    if (days < 0) return { label: 'Scaduto', variant: 'destructive' };
    if (days <= 7) return { label: `${days}g`, variant: 'destructive' };
    if (days <= 30) return { label: `${days}g`, variant: 'warning' };
    return null;
  };

  const filteredContracts = contracts.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = !filterClient || c.client_id === filterClient;
    
    // Filtro BU - controlla se il contratto ha prodotti di quella BU
    let matchesBU = filterBUs.length === 0;
    if (!matchesBU) {
      const contractProducts = contractProductsMap[c.id] || [];
      matchesBU = contractProducts.some(cp => {
        const product = products.find(p => p.id === cp.product_id);
        return product && filterBUs.includes(product.business_unit_id);
      });
    }
    
    // Filtro Prodotti - controlla se il contratto contiene quei prodotti
    let matchesProduct = filterProducts.length === 0;
    if (!matchesProduct) {
      const contractProducts = contractProductsMap[c.id] || [];
      matchesProduct = contractProducts.some(cp => filterProducts.includes(cp.product_id));
    }
    
    // Filtro Account Manager - controlla se il cliente ha quell'account assegnato
    let matchesAccount = filterAccounts.length === 0;
    if (!matchesAccount) {
      const client = clients.find(cl => cl.id === c.client_id);
      matchesAccount = client && filterAccounts.includes(client.assigned_account_id);
    }

    // Filtro Settore (dal cliente)
    let matchesSettore = filterSettori.length === 0;
    if (!matchesSettore) {
      const client = clients.find(cl => cl.id === c.client_id);
      matchesSettore = filterSettori.includes((client?.settore) || '__none__');
    }
    
    // Filtro Campi Personalizzati
    let matchesCF = true;
    for (const [fid, vals] of Object.entries(cfFilters)) {
      if (!vals || vals.length === 0) continue;
      const cv = (c.custom_fields || {})[fid] || '';
      const hasUnspecified = vals.includes('__unspecified__');
      const otherVals = vals.filter(v => v !== '__unspecified__');
      if (hasUnspecified && !cv) continue;
      if (otherVals.includes(cv)) continue;
      matchesCF = false;
      break;
    }
    
    return matchesSearch && matchesClient && matchesBU && matchesProduct && matchesAccount && matchesSettore && matchesCF;
  });

  // Apply signing date filter
  const dateFilteredContracts = filteredContracts.filter(c => {
    if (!signingDateFrom && !signingDateTo) return true;
    const sd = c.signing_date?.split('T')[0] || '';
    if (!sd) return false;
    if (signingDateFrom && sd < signingDateFrom) return false;
    if (signingDateTo && sd > signingDateTo) return false;
    return true;
  });

  // Check if any filter is active for the main tab
  const hasActiveMainFilters = searchTerm || filterClient || filterBUs.length > 0 || filterProducts.length > 0 || filterAccounts.length > 0 || filterSettori.length > 0 || signingDateFrom || signingDateTo || Object.values(cfFilters).some(v => v?.length);

  // Reset all main filters
  const resetMainFilters = () => {
    setSearchTerm('');
    setFilterClient('');
    setFilterBUs([]);
    setFilterProducts([]);
    setFilterAccounts([]);
    setFilterSettori([]);
    setSigningDatePreset('all');
    setSigningDateFrom('');
    setSigningDateTo('');
    setCfFilters({});
  };

  // ─── Export CSV ────────────────────────────────────────────
  const openExportDialog = async () => {
    try {
      const res = await getContractsExportFields();
      setExportFields(res.data);
      setExportDialogOpen(true);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile caricare i campi', variant: 'destructive' });
    }
  };

  const toggleExportKey = (key) => {
    setSelectedExportKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleExportGroup = (groupKeys, allSelected) => {
    setSelectedExportKeys(prev => {
      if (allSelected) return prev.filter(k => !groupKeys.includes(k));
      const merged = new Set([...prev, ...groupKeys]);
      return Array.from(merged);
    });
  };

  const runExport = async () => {
    if (selectedExportKeys.length === 0) {
      toast({ title: 'Nessun campo', description: 'Seleziona almeno un campo da esportare', variant: 'destructive' });
      return;
    }
    if (dateFilteredContracts.length === 0) {
      toast({ title: 'Nessun contratto', description: 'Nessun contratto da esportare con i filtri attuali', variant: 'destructive' });
      return;
    }
    setExporting(true);
    try {
      // Ordina l'ordine delle colonne seguendo l'ordine di visualizzazione nel dialog
      const allOrdered = [
        ...exportFields.contract_std.map(f => f.key),
        ...exportFields.contract_custom.map(f => f.key),
        ...exportFields.client_std.map(f => f.key),
        ...exportFields.client_custom.map(f => f.key),
      ];
      const orderedKeys = allOrdered.filter(k => selectedExportKeys.includes(k));
      const res = await exportContractsCsv({
        contract_ids: dateFilteredContracts.map(c => c.id),
        field_keys: orderedKeys,
      });
      // Save preferences
      try { localStorage.setItem('contracts_export_selected_keys_v2', JSON.stringify(selectedExportKeys)); } catch {}
      // Download blob
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      a.download = `contratti_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Export completato', description: `${dateFilteredContracts.length} contratti esportati` });
      setExportDialogOpen(false);
    } catch (err) {
      toast({ title: 'Errore export', description: err.response?.data?.detail || 'Export fallito', variant: 'destructive' });
    } finally { setExporting(false); }
  };

  // Funzione per gestire il click sull'intestazione della colonna
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Se già ordinato per questa colonna, inverti la direzione
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Altrimenti, ordina per la nuova colonna in ordine ascendente
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Funzione per ottenere l'icona di ordinamento
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Funzione per ordinare i contratti
  const sortContracts = (data) => {
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'client':
          aValue = getClientName(a.client_id)?.toLowerCase() || '';
          bValue = getClientName(b.client_id)?.toLowerCase() || '';
          break;
        case 'signing_date':
          aValue = a.signing_date ? new Date(a.signing_date) : new Date(0);
          bValue = b.signing_date ? new Date(b.signing_date) : new Date(0);
          break;
        case 'value':
          aValue = getContractTotalValue(a.id) || 0;
          bValue = getContractTotalValue(b.id) || 0;
          break;
        default:
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  const renderContractRows = (data, showClient = true) => {
    const sortedData = sortContracts(data);
    const getAccountName = (contractClientId) => {
      const cl = clients.find(c => c.id === contractClientId);
      if (!cl?.assigned_account_id) return '-';
      const acc = accountUsers.find(u => u.id === cl.assigned_account_id);
      return acc?.name || '-';
    };
    const getCfValue = (contract, cfId) => {
      return (contract.custom_fields || {})[cfId] || '';
    };
    // Dynamic CF columns visible
    const visibleCfCols = allContractCFs.filter(f => visibleColumns[`cf_${f.id}`]);
    const allSelected = sortedData.length > 0 && sortedData.every(c => selectedContracts.has(c.id));
    
    return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <Checkbox checked={allSelected} onCheckedChange={() => toggleSelectAll(sortedData)} data-testid="select-all-contracts" />
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-50 select-none"
            onClick={() => handleSort('name')}
          >
            <div className="flex items-center">
              Nome
              {getSortIcon('name')}
            </div>
          </TableHead>
          {showClient && visibleColumns.client !== false && (
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('client')}
            >
              <div className="flex items-center">
                Cliente
                {getSortIcon('client')}
              </div>
            </TableHead>
          )}
          {visibleColumns.products !== false && <TableHead>Prodotti</TableHead>}
          {visibleColumns.value !== false && (
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('value')}
            >
              <div className="flex items-center">
                Valore
                {getSortIcon('value')}
              </div>
            </TableHead>
          )}
          {visibleColumns.signing_date !== false && (
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('signing_date')}
            >
              <div className="flex items-center">
                Data Firma
                {getSortIcon('signing_date')}
              </div>
            </TableHead>
          )}
          {visibleColumns.account_manager && <TableHead>Account Manager</TableHead>}
          {visibleCfCols.map(cf => <TableHead key={cf.id}>{cf.name}</TableHead>)}
          {hasRole(['admin', 'hr', 'project_manager', 'account']) && <TableHead className="w-12"></TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedData.map((contract) => {
          const productsInfo = getContractProductsInfo(contract.id);
          const totalValue = getContractTotalValue(contract.id);
          return (
            <TableRow key={contract.id} data-testid={`contract-row-${contract.id}`} className={selectedContracts.has(contract.id) ? 'bg-orange-50/50' : ''}>
              <TableCell>
                <Checkbox checked={selectedContracts.has(contract.id)} onCheckedChange={() => toggleSelectContract(contract.id)} />
              </TableCell>
              <TableCell className="font-medium">{contract.name}</TableCell>
              {showClient && visibleColumns.client !== false && <TableCell>{getClientName(contract.client_id)}</TableCell>}
              {visibleColumns.products !== false && (
                <TableCell>
                  {productsInfo ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        {productsInfo.total} prodott{productsInfo.total === 1 ? 'o' : 'i'}
                      </Badge>
                      {productsInfo.expired > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {productsInfo.expired} scadut{productsInfo.expired === 1 ? 'o' : 'i'}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">Nessun prodotto</span>
                  )}
                </TableCell>
              )}
              {visibleColumns.value !== false && <TableCell>{formatCurrency(totalValue)}</TableCell>}
              {visibleColumns.signing_date !== false && (
                <TableCell>
                  {contract.signing_date ? (
                    <span className="text-sm">
                      {format(parseISO(contract.signing_date), 'dd/MM/yyyy', { locale: it })}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </TableCell>
              )}
              {visibleColumns.account_manager && (
                <TableCell className="text-sm">{getAccountName(contract.client_id)}</TableCell>
              )}
              {visibleCfCols.map(cf => (
                <TableCell key={cf.id} className="text-sm">{getCfValue(contract, cf.id) || <span className="text-gray-400">-</span>}</TableCell>
              ))}
              {hasRole(['admin', 'hr', 'project_manager', 'account']) && (
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleOpenDialog(contract)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifica
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleOpenProductDialog(contract)}>
                        <Package className="w-4 h-4 mr-2" />
                        Gestisci Prodotti
                      </DropdownMenuItem>
                      {!hasRole(['hr']) && (
                        <DropdownMenuItem onClick={() => handleDelete(contract)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
  };

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contratti</h1>
          <p className="text-gray-500">Gestisci i contratti e monitora le scadenze</p>
        </div>
        {hasRole(['admin', 'hr', 'project_manager', 'account']) && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openExportDialog} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Esporta CSV
            </Button>
            <Button onClick={() => handleOpenDialog()} data-testid="add-contract-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Contratto
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">Tutti</TabsTrigger>
          <TabsTrigger value="expiring" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            In Scadenza ({expiringContracts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4">
                {/* Riga preset periodo (data firma) */}
                <PeriodPresets
                  activePreset={signingDatePreset}
                  onPresetChange={handleSigningDatePresetChange}
                  showCustomDates={signingDatePreset === 'custom'}
                  startDate={signingDateFrom}
                  endDate={signingDateTo}
                  onStartDateChange={(v) => { setSigningDateFrom(v); setSigningDatePreset('custom'); }}
                  onEndDateChange={(v) => { setSigningDateTo(v); setSigningDatePreset('custom'); }}
                  testIdPrefix="contracts-signing"
                />
                {/* Prima riga: Ricerca e Cliente */}
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Cerca contratti..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-contracts"
                    />
                  </div>
                  <div className="w-[220px]">
                    <Combobox
                      options={[
                        { value: '', label: 'Tutti i clienti' },
                        ...clients.map(c => ({ value: c.id, label: c.company_name }))
                      ]}
                      value={filterClient}
                      onChange={(v) => setFilterClient(v)}
                      placeholder="Filtra per cliente"
                      searchPlaceholder="Cerca cliente..."
                      emptyText="Nessun cliente trovato"
                    />
                  </div>
                  
                  {/* Reset filters */}
                  {hasActiveMainFilters && (
                    <Button variant="ghost" size="sm" onClick={resetMainFilters} className="text-gray-500">
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  )}
                </div>
                
                {/* Seconda riga: Filtri avanzati */}
                <div className="flex flex-wrap gap-3">
                  {/* Business Unit - Multi-select */}
                  <div className="w-[200px]">
                    <MultiSelect
                      options={businessUnits.map(bu => ({ value: bu.id, label: bu.name }))}
                      selected={filterBUs}
                      onChange={setFilterBUs}
                      placeholder="Business Unit"
                    />
                  </div>
                  
                  {/* Prodotto - Multi-select */}
                  <div className="w-[200px]">
                    <MultiSelect
                      options={products.map(p => ({ value: p.id, label: p.name }))}
                      selected={filterProducts}
                      onChange={setFilterProducts}
                      placeholder="Prodotto"
                    />
                  </div>
                  
                  {/* Account Manager - Multi-select */}
                  <div className="w-[200px]">
                    <MultiSelect
                      options={accountUsers.map(u => ({ value: u.id, label: u.name }))}
                      selected={filterAccounts}
                      onChange={setFilterAccounts}
                      placeholder="Account Manager"
                    />
                  </div>

                  {/* Settore (dal cliente) - Multi-select */}
                  <div className="w-[200px]">
                    <MultiSelect
                      options={[
                        { value: '__none__', label: 'Non specificato' },
                        ...SETTORI.map(s => ({ value: s, label: s })),
                      ]}
                      selected={filterSettori}
                      onChange={setFilterSettori}
                      placeholder="Settore"
                      data-testid="filter-settori"
                    />
                  </div>
                  
                  {/* Campi personalizzati - Multi-select */}
                  {contractCFs.map(cf => (
                    <div key={cf.id} className="w-[200px]">
                      <MultiSelect
                        options={[
                          { value: '__unspecified__', label: 'Non specificato' },
                          ...(cf.options || []).map(o => ({ value: o, label: o }))
                        ]}
                        selected={cfFilters[cf.id] || []}
                        onChange={v => setCfFilters(prev => {
                          const next = { ...prev };
                          if (v.length) next[cf.id] = v; else delete next[cf.id];
                          return next;
                        })}
                        placeholder={cf.name}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Selection bar + Counter */}
              {dateFilteredContracts.length > 0 && (
                <div className="mb-4 px-1 space-y-2">
                  {selectedContracts.size > 0 && (
                    <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5" data-testid="selection-bar">
                      <span className="text-sm font-medium text-orange-700">
                        {selectedContracts.size} selezionat{selectedContracts.size === 1 ? 'o' : 'i'}
                      </span>
                      <span className="text-sm font-semibold text-orange-800">
                        Valore: {formatCurrency(selectedTotal)}
                      </span>
                      <div className="flex-1" />
                      <Button size="sm" variant="outline" className="text-xs border-orange-300 text-orange-700 hover:bg-orange-100 gap-1.5"
                        onClick={() => { setBulkCfValues({}); setBulkEditOpen(true); }} data-testid="bulk-edit-btn">
                        <Pencil className="w-3 h-3" /> Modifica Campi
                      </Button>
                      <Button size="sm" variant="ghost" className="text-xs text-gray-500"
                        onClick={() => setSelectedContracts(new Set())} data-testid="clear-selection-btn">
                        <X className="w-3 h-3 mr-1" /> Deseleziona
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{dateFilteredContracts.length} contratt{dateFilteredContracts.length === 1 ? 'o' : 'i'}</span>
                    <span className="font-semibold text-gray-800">
                      Totale: {formatCurrency(dateFilteredContracts.reduce((sum, c) => sum + (getContractTotalValue(c.id) || 0), 0))}
                    </span>
                  </div>
                  {/* Column config button */}
                  <div className="relative">
                    <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => setColMenuOpen(!colMenuOpen)} data-testid="col-config-btn">
                      <Filter className="w-3 h-3" /> Colonne
                    </Button>
                    {colMenuOpen && (
                      <div className="absolute right-0 top-9 z-50 bg-white border rounded-lg shadow-lg p-3 min-w-[200px]" data-testid="col-config-menu">
                        <div className="text-[11px] font-medium text-gray-400 uppercase mb-2">Colonne visibili</div>
                        {[
                          { key: 'name', label: 'Nome', locked: true },
                          { key: 'client', label: 'Cliente' },
                          { key: 'products', label: 'Prodotti' },
                          { key: 'value', label: 'Valore' },
                          { key: 'signing_date', label: 'Data Firma' },
                          { key: 'account_manager', label: 'Account Manager' },
                          ...allContractCFs.map(f => ({ key: `cf_${f.id}`, label: f.name })),
                        ].map(col => (
                          <label key={col.key} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                            <Checkbox checked={col.locked || visibleColumns[col.key] !== false} disabled={col.locked}
                              onCheckedChange={() => !col.locked && toggleColumn(col.key)} />
                            {col.label}
                          </label>
                        ))}
                        <Button variant="ghost" size="sm" className="w-full mt-2 text-xs" onClick={() => setColMenuOpen(false)}>Chiudi</Button>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              )}
              {dateFilteredContracts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="mt-4 text-gray-500">Nessun contratto trovato</p>
                </div>
              ) : (
                renderContractRows(dateFilteredContracts)
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expiring">
          <div className="space-y-6">
            {/* Advanced Filters */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Filtri
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={resetExpiryFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search and Status Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Ricerca</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Cerca prodotto, contratto, cliente, PM..."
                        value={expiryFilters.search}
                        onChange={(e) => handleExpiryFilterChange('search', e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <MultiSelect
                      options={statusOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                      selected={filterStatus}
                      onChange={setFilterStatus}
                      placeholder="Tutti gli stati"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Business Unit</Label>
                    <MultiSelect
                      options={businessUnits.map(bu => ({ value: bu.id, label: bu.name }))}
                      selected={expiryFilters.business_unit_ids}
                      onChange={(values) => setExpiryFilters(prev => ({ ...prev, business_unit_ids: values }))}
                      placeholder="Tutte"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Prodotto</Label>
                    <MultiSelect
                      options={products
                        .filter(p => expiryFilters.business_unit_ids.length === 0 || expiryFilters.business_unit_ids.includes(p.business_unit_id))
                        .map(p => ({ value: p.id, label: p.name }))}
                      selected={expiryFilters.product_ids}
                      onChange={(values) => setExpiryFilters(prev => ({ ...prev, product_ids: values }))}
                      placeholder="Tutti"
                    />
                  </div>
                </div>
                
                {/* PM and Date Filters Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Project Manager</Label>
                    <MultiSelect
                      options={pmUsers.map(pm => ({ value: pm.id, label: pm.name }))}
                      selected={expiryFilters.pm_ids}
                      onChange={(values) => setExpiryFilters(prev => ({ ...prev, pm_ids: values }))}
                      placeholder="Tutti"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Manager</Label>
                    <MultiSelect
                      options={accountUsers.map(acc => ({ value: acc.id, label: acc.name }))}
                      selected={expiryFilters.account_ids}
                      onChange={(values) => setExpiryFilters(prev => ({ ...prev, account_ids: values }))}
                      placeholder="Tutti"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Periodo Scadenza</Label>
                    <Select value={expiryFilters.expiryDatePreset || 'all'} onValueChange={(v) => handleExpiryDatePresetChange(v === 'all' ? '' : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tutti i periodi" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti i periodi</SelectItem>
                        {datePresets.map((preset) => (
                          <SelectItem key={preset.label} value={preset.label}>{preset.label}</SelectItem>
                        ))}
                        <SelectItem value="custom">Personalizzato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Periodo Esito</Label>
                    <Select value={expiryFilters.datePreset || 'all'} onValueChange={(v) => handleDatePresetChange(v === 'all' ? '' : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tutti i periodi" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti i periodi</SelectItem>
                        {datePresets.map((preset) => (
                          <SelectItem key={preset.label} value={preset.label}>{preset.label}</SelectItem>
                        ))}
                        <SelectItem value="custom">Personalizzato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Date Range Filters Row (conditional) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {(expiryFilters.expiryDatePreset === 'custom' || (!expiryFilters.expiryDatePreset && (expiryFilters.expiry_date_from || expiryFilters.expiry_date_to))) && (
                    <>
                      <div className="space-y-2">
                        <Label>Scadenza Da</Label>
                        <Input
                          type="date"
                          value={expiryFilters.expiry_date_from}
                          onChange={(e) => handleExpiryFilterChange('expiry_date_from', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Scadenza A</Label>
                        <Input
                          type="date"
                          value={expiryFilters.expiry_date_to}
                          onChange={(e) => handleExpiryFilterChange('expiry_date_to', e.target.value)}
                        />
                      </div>
                    </>
                  )}
                  {(expiryFilters.datePreset === 'custom' || (!expiryFilters.datePreset && (expiryFilters.outcome_date_from || expiryFilters.outcome_date_to))) && (
                    <>
                      <div className="space-y-2">
                        <Label>Data Esito Da</Label>
                        <Input
                          type="date"
                          value={expiryFilters.outcome_date_from}
                          onChange={(e) => handleExpiryFilterChange('outcome_date_from', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Esito A</Label>
                        <Input
                          type="date"
                          value={expiryFilters.outcome_date_to}
                          onChange={(e) => handleExpiryFilterChange('outcome_date_to', e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>


            {/* Scaduti */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <Clock className="w-5 h-5" />
                    Scaduti
                  </CardTitle>
                  <div className="relative">
                    <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => setExpColMenuOpen(!expColMenuOpen)} data-testid="exp-col-config-btn">
                      <Filter className="w-3 h-3" /> Colonne
                    </Button>
                    {expColMenuOpen && (
                      <div className="absolute right-0 top-9 z-50 bg-white border rounded-lg shadow-lg p-3 min-w-[200px]" data-testid="exp-col-config-menu">
                        <div className="text-[11px] font-medium text-gray-400 uppercase mb-2">Colonne visibili</div>
                        {[
                          { key: 'product', label: 'Prodotto', locked: true },
                          { key: 'contract', label: 'Contratto' },
                          { key: 'project', label: 'Progetto' },
                          { key: 'client', label: 'Cliente' },
                          { key: 'pm', label: 'Project Manager' },
                          { key: 'expiry_date', label: 'Data Scadenza' },
                          { key: 'status', label: 'Stato' },
                          { key: 'outcome_date', label: 'Data Esito' },
                          { key: 'value', label: 'Valore' },
                          { key: 'renewal_value', label: 'Valore Rinnovo' },
                          { key: 'account_manager', label: 'Account Manager' },
                          ...allContractCFs.map(f => ({ key: `cf_${f.id}`, label: f.name })),
                        ].map(col => (
                          <label key={col.key} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                            <Checkbox checked={col.locked || visibleExpColumns[col.key] !== false} disabled={col.locked}
                              onCheckedChange={() => !col.locked && toggleExpColumn(col.key)} />
                            {col.label}
                          </label>
                        ))}
                        <Button variant="ghost" size="sm" className="w-full mt-2 text-xs" onClick={() => setExpColMenuOpen(false)}>Chiudi</Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {expiredContracts.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-10 h-10 mx-auto text-gray-300" />
                    <p className="mt-2 text-gray-500 text-sm">Nessun prodotto scaduto</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Prodotto</TableHead>
                        {visibleExpColumns.contract !== false && <TableHead>Contratto</TableHead>}
                        {visibleExpColumns.project !== false && <TableHead>Progetto</TableHead>}
                        {visibleExpColumns.client !== false && <TableHead>Cliente</TableHead>}
                        {visibleExpColumns.pm !== false && <TableHead>Project Manager</TableHead>}
                        {visibleExpColumns.expiry_date !== false && <TableHead>Scaduto il</TableHead>}
                        {visibleExpColumns.status !== false && <TableHead>Stato</TableHead>}
                        {visibleExpColumns.outcome_date !== false && <TableHead>Data Esito</TableHead>}
                        {visibleExpColumns.value !== false && <TableHead>Valore</TableHead>}
                        {visibleExpColumns.renewal_value !== false && <TableHead>Valore Rinnovo</TableHead>}
                        {visibleExpColumns.account_manager && <TableHead>Account Manager</TableHead>}
                        {allContractCFs.filter(f => visibleExpColumns[`cf_${f.id}`]).map(cf => <TableHead key={cf.id}>{cf.name}</TableHead>)}
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expiredContracts.map((item) => {
                        const statusInfo = statusOptions.find(s => s.value === item.status) || statusOptions[0];
                        const expAccName = (() => { const cl = clients.find(c => c.id === item.client_id); const acc = cl ? accountUsers.find(u => u.id === cl.assigned_account_id) : null; return acc?.name || '-'; })();
                        return (
                          <TableRow key={item.contract_product_id}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            {visibleExpColumns.contract !== false && <TableCell>{item.contract_name}</TableCell>}
                            {visibleExpColumns.project !== false && (
                              <TableCell>
                                {item.project_names && item.project_names.length > 0 
                                  ? item.project_names.join(', ') 
                                  : <span className="text-gray-400">-</span>}
                              </TableCell>
                            )}
                            {visibleExpColumns.client !== false && <TableCell>{item.client_name}</TableCell>}
                            {visibleExpColumns.pm !== false && (
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <span>
                                    {item.pm_names && item.pm_names.length > 0 
                                      ? item.pm_names.join(', ') 
                                      : <span className="text-gray-400">-</span>}
                                  </span>
                                  {hasRole(['admin']) && item.status !== 'open' && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6 ml-1"
                                      onClick={() => openPmSnapshotDialog(item)} title="Modifica Project Manager">
                                      <Pencil className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                            {visibleExpColumns.expiry_date !== false && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {item.end_date ? format(parseISO(item.end_date), 'dd/MM/yyyy') : '-'}
                                  <Badge variant="secondary" className="text-gray-600">
                                    {item.days_since_expiry} giorni fa
                                  </Badge>
                                </div>
                              </TableCell>
                            )}
                            {visibleExpColumns.status !== false && (
                              <TableCell><Badge className={statusInfo.color}>{statusInfo.label}</Badge></TableCell>
                            )}
                            {visibleExpColumns.outcome_date !== false && (
                              <TableCell>
                                {item.outcome_date ? format(parseISO(item.outcome_date), 'dd/MM/yyyy') : <span className="text-gray-400">-</span>}
                              </TableCell>
                            )}
                            {visibleExpColumns.value !== false && <TableCell>{formatCurrency(item.value)}</TableCell>}
                            {visibleExpColumns.renewal_value !== false && (
                              <TableCell className={item.status === 'renewed' && item.renewal_value ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                                {item.status === 'renewed' ? (item.renewal_value ? formatCurrency(item.renewal_value) : '—') : '—'}
                              </TableCell>
                            )}
                            {visibleExpColumns.account_manager && <TableCell className="text-sm">{expAccName}</TableCell>}
                            {allContractCFs.filter(f => visibleExpColumns[`cf_${f.id}`]).map(cf => (
                              <TableCell key={cf.id} className="text-sm">{(item.custom_fields || {})[cf.id] || <span className="text-gray-400">-</span>}</TableCell>
                            ))}
                            <TableCell>
                              {hasRole(['admin', 'account']) && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {statusOptions.map((opt) => (
                                      <DropdownMenuItem 
                                        key={opt.value}
                                        onClick={() => handleUpdateProductStatus(item.contract_id, item.contract_product_id, opt.value)}
                                        disabled={item.status === opt.value}
                                      >
                                        Segna come {opt.label}
                                      </DropdownMenuItem>
                                    ))}
                                    {hasRole(['admin', 'account']) && item.status === 'renewed' && (
                                      <DropdownMenuItem onClick={() => handleEditRenewal(item)}>
                                        {item.renewal_contract_id ? 'Modifica Contratto Rinnovo' : 'Aggiungi Contratto Rinnovo'}
                                      </DropdownMenuItem>
                                    )}
                                    {hasRole(['admin']) && item.status !== 'open' && item.outcome_date && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => handleEditOutcomeDate(item)}>
                                          Modifica Data Esito
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* In Scadenza */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-600">
                  <Calendar className="w-5 h-5" />
                  In Scadenza
                </CardTitle>
              </CardHeader>
              <CardContent>
                {expiringContracts.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-10 h-10 mx-auto text-gray-300" />
                    <p className="mt-2 text-gray-500 text-sm">Nessun prodotto in scadenza</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Prodotto</TableHead>
                        {visibleExpColumns.contract !== false && <TableHead>Contratto</TableHead>}
                        {visibleExpColumns.project !== false && <TableHead>Progetto</TableHead>}
                        {visibleExpColumns.client !== false && <TableHead>Cliente</TableHead>}
                        {visibleExpColumns.pm !== false && <TableHead>Project Manager</TableHead>}
                        {visibleExpColumns.expiry_date !== false && <TableHead>Scadenza</TableHead>}
                        {visibleExpColumns.status !== false && <TableHead>Stato</TableHead>}
                        {visibleExpColumns.outcome_date !== false && <TableHead>Data Esito</TableHead>}
                        {visibleExpColumns.value !== false && <TableHead>Valore</TableHead>}
                        {visibleExpColumns.renewal_value !== false && <TableHead>Valore Rinnovo</TableHead>}
                        {visibleExpColumns.account_manager && <TableHead>Account Manager</TableHead>}
                        {allContractCFs.filter(f => visibleExpColumns[`cf_${f.id}`]).map(cf => <TableHead key={cf.id}>{cf.name}</TableHead>)}
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expiringContracts.map((item) => {
                        const statusInfo = statusOptions.find(s => s.value === item.status) || statusOptions[0];
                        const expAccName2 = (() => { const cl = clients.find(c => c.id === item.client_id); const acc = cl ? accountUsers.find(u => u.id === cl.assigned_account_id) : null; return acc?.name || '-'; })();
                        return (
                          <TableRow key={item.contract_product_id}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            {visibleExpColumns.contract !== false && <TableCell>{item.contract_name}</TableCell>}
                            {visibleExpColumns.project !== false && (
                              <TableCell>
                                {item.project_names && item.project_names.length > 0 
                                  ? item.project_names.join(', ') 
                                  : <span className="text-gray-400">-</span>}
                              </TableCell>
                            )}
                            {visibleExpColumns.client !== false && <TableCell>{item.client_name}</TableCell>}
                            {visibleExpColumns.pm !== false && (
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <span>
                                    {item.pm_names && item.pm_names.length > 0 
                                      ? item.pm_names.join(', ') 
                                      : <span className="text-gray-400">-</span>}
                                  </span>
                                  {hasRole(['admin']) && item.status !== 'open' && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6 ml-1"
                                      onClick={() => openPmSnapshotDialog(item)} title="Modifica Project Manager">
                                      <Pencil className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                            {visibleExpColumns.expiry_date !== false && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {item.end_date ? format(parseISO(item.end_date), 'dd/MM/yyyy') : '-'}
                                  <Badge variant={item.days_until_expiry <= 7 ? 'destructive' : 'outline'} className={item.days_until_expiry <= 7 ? '' : 'text-orange-600 border-orange-300'}>
                                    {item.days_until_expiry} giorni
                                  </Badge>
                                </div>
                              </TableCell>
                            )}
                            {visibleExpColumns.status !== false && (
                              <TableCell><Badge className={statusInfo.color}>{statusInfo.label}</Badge></TableCell>
                            )}
                            {visibleExpColumns.outcome_date !== false && (
                              <TableCell>
                                {item.outcome_date ? format(parseISO(item.outcome_date), 'dd/MM/yyyy') : <span className="text-gray-400">-</span>}
                              </TableCell>
                            )}
                            {visibleExpColumns.value !== false && <TableCell>{formatCurrency(item.value)}</TableCell>}
                            {visibleExpColumns.renewal_value !== false && (
                              <TableCell className={item.status === 'renewed' && item.renewal_value ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                                {item.status === 'renewed' ? (item.renewal_value ? formatCurrency(item.renewal_value) : '—') : '—'}
                              </TableCell>
                            )}
                            {visibleExpColumns.account_manager && <TableCell className="text-sm">{expAccName2}</TableCell>}
                            {allContractCFs.filter(f => visibleExpColumns[`cf_${f.id}`]).map(cf => (
                              <TableCell key={cf.id} className="text-sm">{(item.custom_fields || {})[cf.id] || <span className="text-gray-400">-</span>}</TableCell>
                            ))}
                            <TableCell>
                              {hasRole(['admin', 'account']) && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {statusOptions.map((opt) => (
                                      <DropdownMenuItem 
                                        key={opt.value}
                                        onClick={() => handleUpdateProductStatus(item.contract_id, item.contract_product_id, opt.value)}
                                        disabled={item.status === opt.value}
                                      >
                                        Segna come {opt.label}
                                      </DropdownMenuItem>
                                    ))}
                                    {hasRole(['admin', 'account']) && item.status === 'renewed' && (
                                      <DropdownMenuItem onClick={() => handleEditRenewal(item)}>
                                        {item.renewal_contract_id ? 'Modifica Contratto Rinnovo' : 'Aggiungi Contratto Rinnovo'}
                                      </DropdownMenuItem>
                                    )}
                                    {hasRole(['admin']) && item.status !== 'open' && item.outcome_date && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => handleEditOutcomeDate(item)}>
                                          Modifica Data Esito
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingContract ? 'Modifica Contratto' : 'Nuovo Contratto'}</DialogTitle>
            <DialogDescription>
              {editingContract ? 'Modifica i dati del contratto' : 'Inserisci i dati del nuovo contratto'}
            </DialogDescription>
          </DialogHeader>
          <form id="contract-form" onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Contratto *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="contract-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Combobox
                options={clients.map(c => ({ value: c.id, label: c.company_name }))}
                value={formData.client_id}
                onChange={(v) => setFormData({ ...formData, client_id: v })}
                placeholder="Seleziona cliente"
                searchPlaceholder="Cerca cliente..."
                emptyText="Nessun cliente trovato"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signing_date">Data Firma *</Label>
              <Input
                id="signing_date"
                type="date"
                value={formData.signing_date}
                onChange={(e) => setFormData({ ...formData, signing_date: e.target.value })}
                required
              />
              <p className="text-xs text-gray-500">La data di firma determina il mese di imputazione della vendita</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Note</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p><strong>Nota:</strong> Il valore del contratto e le date di validità sono ora gestiti a livello di singolo prodotto. 
              Usa &quot;Gestisci Prodotti&quot; dopo aver creato il contratto per aggiungere prodotti con i relativi valori e date.</p>
            </div>

            {customFields.length > 0 && (
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium">Campi Personalizzati</h4>
                {customFields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label>{field.name} {field.is_required && '*'}</Label>
                    {field.field_type === 'text' && (
                      <Input
                        value={formData.custom_fields[field.id] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: e.target.value },
                          })
                        }
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'number' && (
                      <Input
                        type="number"
                        value={formData.custom_fields[field.id] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: e.target.value },
                          })
                        }
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'select' && (
                      <Select
                        value={formData.custom_fields[field.id] || ''}
                        onValueChange={(v) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: v === '__none__' ? '' : v },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          {!field.is_required && (
                            <SelectItem value="__none__">
                              <span className="text-gray-400">Nessuno</span>
                            </SelectItem>
                          )}
                          {field.options?.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {field.field_type === 'boolean' && (
                      <Checkbox
                        checked={formData.custom_fields[field.id] || false}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: checked },
                          })
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </form>
          <DialogFooter className="border-t pt-4 mt-2">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" form="contract-form" disabled={saving} data-testid="save-contract-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingContract ? 'Salva Modifiche' : 'Crea Contratto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Management Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={(open) => {
        setProductDialogOpen(open);
        if (!open) {
          setEditingContractProduct(null);
          setProductFormData({ product_id: '', value: '', start_date: '', end_date: '' });
          setAttachments([]);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Gestisci Contratto - {selectedContractForProducts?.name}
            </DialogTitle>
            <DialogDescription>
              Gestisci prodotti e allegati del contratto
            </DialogDescription>
          </DialogHeader>
          
          {selectedContractForProducts && (
            <div className="space-y-6">
              {/* Current products */}
              <div>
                <h4 className="font-medium mb-3">Prodotti nel contratto</h4>
                {(contractProductsMap[selectedContractForProducts.id] || []).length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Nessun prodotto associato</p>
                ) : (
                  <div className="space-y-2">
                    {(contractProductsMap[selectedContractForProducts.id] || []).map((cp) => (
                      <div key={cp.id} className={`flex items-center justify-between p-3 rounded-lg ${editingContractProduct?.id === cp.id ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'}`}>
                        <div>
                          <p className="font-medium">{cp.product?.name || 'N/A'}</p>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <span>{formatCurrency(cp.value)}</span>
                            {cp.product?.has_expiration && cp.start_date && cp.end_date && (
                              <>
                                <span>•</span>
                                <span>
                                  {format(parseISO(cp.start_date), 'dd/MM/yyyy')} - {format(parseISO(cp.end_date), 'dd/MM/yyyy')}
                                </span>
                                {cp.is_expired && (
                                  <Badge variant="destructive" className="text-xs">Scaduto</Badge>
                                )}
                              </>
                            )}
                            {!cp.product?.has_expiration && (
                              <Badge variant="secondary" className="text-xs">Senza scadenza</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditContractProduct(cp)}
                            disabled={editingContractProduct?.id === cp.id}
                          >
                            <Pencil className="w-4 h-4 text-gray-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRemoveProduct(selectedContractForProducts.id, cp.id)}
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add/Edit product form */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">
                  {editingContractProduct ? `Modifica: ${editingContractProduct.product?.name}` : 'Aggiungi prodotto'}
                </h4>
                {products.length === 0 ? (
                  <p className="text-sm text-gray-500">Nessun prodotto disponibile. Crea prodotti in Impostazioni.</p>
                ) : (
                  <form onSubmit={handleSaveProduct} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {!editingContractProduct && (
                        <div className="space-y-2">
                          <Label>Prodotto *</Label>
                          <Select
                            value={productFormData.product_id}
                            onValueChange={(v) => setProductFormData({ ...productFormData, product_id: v })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona prodotto" />
                            </SelectTrigger>
                            <SelectContent>
                              {products.filter(p => p.is_active).map((product) => (
                                <SelectItem key={product.id} value={product.id}>
                                  <div className="flex items-center gap-2">
                                    {product.name}
                                    {product.has_expiration ? (
                                      <Badge variant="outline" className="text-xs">con scadenza</Badge>
                                    ) : (
                                      <Badge variant="secondary" className="text-xs">senza scadenza</Badge>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className={`space-y-2 ${editingContractProduct ? 'col-span-2' : ''}`}>
                        <Label>Valore (€) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={productFormData.value}
                          onChange={(e) => setProductFormData({ ...productFormData, value: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    {/* Show date fields only for products with expiration */}
                    {((editingContractProduct && editingContractProduct.product?.has_expiration) || 
                      (!editingContractProduct && productFormData.product_id && products.find(p => p.id === productFormData.product_id)?.has_expiration)) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Data Inizio operatività *</Label>
                          <Input
                            type="date"
                            value={productFormData.start_date}
                            onChange={(e) => setProductFormData({ ...productFormData, start_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Data fine operatività *</Label>
                          <Input
                            type="date"
                            value={productFormData.end_date}
                            onChange={(e) => setProductFormData({ ...productFormData, end_date: e.target.value })}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      {editingContractProduct && (
                        <Button type="button" variant="outline" onClick={handleCancelEditProduct} className="flex-1">
                          Annulla
                        </Button>
                      )}
                      <Button type="submit" disabled={saving} className="flex-1">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : editingContractProduct ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        {editingContractProduct ? 'Salva Modifiche' : 'Aggiungi Prodotto'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>

              {/* Total value */}
              <div className="border-t pt-4 flex justify-between items-center">
                <span className="font-medium">Valore Totale Contratto:</span>
                <span className="text-xl font-bold text-orange-600">
                  {formatCurrency(getContractTotalValue(selectedContractForProducts.id))}
                </span>
              </div>

              {/* Attachments Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Allegati
                  </h4>
                  {hasRole(['admin', 'hr', 'account', 'project_manager']) && (
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept="application/pdf"
                        className="hidden"
                        data-testid="attachment-file-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAttachment}
                        data-testid="upload-attachment-btn"
                      >
                        {uploadingAttachment ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Carica PDF
                      </Button>
                    </div>
                  )}
                </div>
                
                {loadingAttachments ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : attachments.length === 0 ? (
                  <p className="text-sm text-gray-500 italic py-2">Nessun allegato</p>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        data-testid={`attachment-${attachment.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <File className="w-8 h-8 text-red-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate" title={attachment.original_filename}>
                              {attachment.original_filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(attachment.file_size)} • Caricato da {attachment.uploaded_by_name || 'N/A'} il {format(parseISO(attachment.created_at), 'dd/MM/yyyy HH:mm')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadAttachment(attachment)}
                            title="Scarica"
                            data-testid={`download-attachment-${attachment.id}`}
                          >
                            <Download className="w-4 h-4 text-blue-500" />
                          </Button>
                          {canDeleteAttachment(attachment) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAttachment(attachment)}
                              title="Elimina"
                              data-testid={`delete-attachment-${attachment.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Formati accettati: PDF • Dimensione massima: 25 MB
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog with Outcome Date */}
      <Dialog open={statusChangeDialog.open} onOpenChange={(open) => {
        if (!open) {
          setStatusChangeDialog({ open: false, contractId: null, contractProductId: null, newStatus: null, outcomeDate: '', renewalContractId: '', renewalContractProductIds: [] });
          setRenewalContractProducts([]);
          setRenewalSearchTerm('');
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Conferma Cambio Stato</DialogTitle>
            <DialogDescription>
              Stai per impostare lo stato a &quot;{statusOptions.find(s => s.value === statusChangeDialog.newStatus)?.label}&quot;.
              Inserisci la data in cui è avvenuto questo esito.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="outcome_date">Data Esito *</Label>
              <Input
                id="outcome_date"
                type="date"
                value={statusChangeDialog.outcomeDate}
                onChange={(e) => setStatusChangeDialog({ ...statusChangeDialog, outcomeDate: e.target.value })}
                required
                data-testid="outcome-date-input"
              />
              <p className="text-xs text-gray-500">
                La data in cui il cliente ha comunicato l&apos;esito (rinnovo, perdita o recesso).
                {statusChangeDialog.newStatus === 'renewed' && (
                  <> Per i rinnovi si aggiorna in automatico alla firma del contratto selezionato.</>
                )}
              </p>
            </div>

            {/* Renewal contract selection - only for 'renewed' status */}
            {statusChangeDialog.newStatus === 'renewed' && (
              <>
                <div className="space-y-2 border-t pt-4">
                  <Label>Contratto di rinnovo *</Label>
                  <p className="text-xs text-gray-500 mb-1">
                    Cerca e seleziona il nuovo contratto che rappresenta il rinnovo.
                  </p>
                  <Input
                    placeholder="Cerca per numero contratto o cliente..."
                    value={renewalSearchTerm}
                    onChange={e => setRenewalSearchTerm(e.target.value)}
                    data-testid="renewal-contract-search"
                  />
                  <div className="border rounded max-h-[160px] overflow-y-auto">
                    {contracts
                      .filter(c => c.id !== statusChangeDialog.contractId)
                      .filter(c => {
                        if (!renewalSearchTerm.trim()) return true;
                        const t = renewalSearchTerm.toLowerCase();
                        return (c.contract_number || '').toLowerCase().includes(t)
                            || (c.client_name || '').toLowerCase().includes(t)
                            || (c.name || '').toLowerCase().includes(t);
                      })
                      .slice(0, 50)
                      .map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleSelectRenewalContract(c.id)}
                          className={`w-full text-left px-3 py-2 text-xs border-b last:border-b-0 hover:bg-orange-50 ${statusChangeDialog.renewalContractId === c.id ? 'bg-orange-100' : ''}`}
                          data-testid={`renewal-contract-option-${c.id}`}
                        >
                          <div className="font-medium">{c.contract_number || c.name}</div>
                          <div className="text-gray-500">{c.client_name || ''} · €{Number(c.total_value || 0).toLocaleString('it-IT')}</div>
                        </button>
                      ))}
                    {contracts.length === 0 && (
                      <p className="text-xs text-gray-400 px-3 py-3">Nessun contratto disponibile.</p>
                    )}
                  </div>
                </div>

                {statusChangeDialog.renewalContractId && (
                  <div className="space-y-2 border-t pt-4">
                    <Label>Prodotti del rinnovo *</Label>
                    <p className="text-xs text-gray-500 mb-1">
                      Seleziona i prodotti del nuovo contratto che fanno riferimento a questo rinnovo (gli altri saranno ignorati).
                    </p>
                    {loadingRenewalProducts ? (
                      <div className="text-xs text-gray-500 py-2">Caricamento prodotti…</div>
                    ) : renewalContractProducts.length === 0 ? (
                      <p className="text-xs text-gray-400">Nessun prodotto in questo contratto.</p>
                    ) : (
                      <div className="border rounded max-h-[160px] overflow-y-auto">
                        {renewalContractProducts.map(cp => {
                          const prod = products.find(p => p.id === cp.product_id);
                          const selected = statusChangeDialog.renewalContractProductIds.includes(cp.id);
                          return (
                            <label
                              key={cp.id}
                              className={`flex items-center gap-2 px-3 py-2 text-xs border-b last:border-b-0 cursor-pointer hover:bg-orange-50 ${selected ? 'bg-orange-50' : ''}`}
                              data-testid={`renewal-product-row-${cp.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleRenewalProduct(cp.id)}
                                className="accent-orange-500"
                              />
                              <div className="flex-1">
                                <div className="font-medium">{prod ? prod.name : cp.product_id}</div>
                                <div className="text-gray-500">€ {Number(cp.value || 0).toLocaleString('it-IT')}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {statusChangeDialog.renewalContractProductIds.length > 0 && (
                      <div className="text-xs text-emerald-700 font-medium pt-1">
                        Importo rinnovo: € {renewalContractProducts
                          .filter(cp => statusChangeDialog.renewalContractProductIds.includes(cp.id))
                          .reduce((sum, cp) => sum + Number(cp.value || 0), 0)
                          .toLocaleString('it-IT')}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setStatusChangeDialog({ open: false, contractId: null, contractProductId: null, newStatus: null, outcomeDate: '', renewalContractId: '', renewalContractProductIds: [] });
              setRenewalContractProducts([]);
            }}>
              Annulla
            </Button>
            <Button 
              onClick={() => handleConfirmStatusChange(
                statusChangeDialog.contractId,
                statusChangeDialog.contractProductId,
                statusChangeDialog.newStatus,
                statusChangeDialog.outcomeDate,
                statusChangeDialog.newStatus === 'renewed' ? statusChangeDialog.renewalContractId : null,
                statusChangeDialog.newStatus === 'renewed' ? statusChangeDialog.renewalContractProductIds : null,
              )}
              disabled={
                !statusChangeDialog.outcomeDate ||
                (statusChangeDialog.newStatus === 'renewed' && (!statusChangeDialog.renewalContractId || statusChangeDialog.renewalContractProductIds.length === 0))
              }
              data-testid="confirm-status-change-btn"
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Outcome Date Dialog */}
      <Dialog open={editOutcomeDateDialog.open} onOpenChange={(open) => {
        if (!open) setEditOutcomeDateDialog({ open: false, contractId: null, contractProductId: null, outcomeDate: '' });
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Modifica Data Esito</DialogTitle>
            <DialogDescription>
              Modifica la data in cui è stato registrato l&apos;esito del contratto.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="edit_outcome_date">Data Esito</Label>
            <Input
              id="edit_outcome_date"
              type="date"
              value={editOutcomeDateDialog.outcomeDate}
              onChange={(e) => setEditOutcomeDateDialog(prev => ({ ...prev, outcomeDate: e.target.value }))}
              data-testid="edit-outcome-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOutcomeDateDialog({ open: false, contractId: null, contractProductId: null, outcomeDate: '' })}>
              Annulla
            </Button>
            <Button onClick={handleConfirmEditOutcomeDate} disabled={!editOutcomeDateDialog.outcomeDate} data-testid="edit-outcome-date-confirm">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Bulk Edit Custom Fields Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Massiva Campi</DialogTitle>
            <DialogDescription>
              Modifica i campi personalizzati per {selectedContracts.size} contratt{selectedContracts.size === 1 ? 'o' : 'i'} selezionat{selectedContracts.size === 1 ? 'o' : 'i'}.
              <br /><span className="font-medium">Valore totale: {formatCurrency(selectedTotal)}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {allContractCFs.map(cf => (
              <div key={cf.id} className="space-y-1.5">
                <Label className="text-sm">{cf.name}</Label>
                {cf.field_type === 'select' ? (
                  <Select value={bulkCfValues[cf.id] || '__no_change__'} onValueChange={v => setBulkCfValues(prev => ({ ...prev, [cf.id]: v === '__no_change__' ? undefined : v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Non modificare" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__no_change__">Non modificare</SelectItem>
                      <SelectItem value="__clear__">Svuota campo</SelectItem>
                      {(cf.options || []).map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="Lascia vuoto per non modificare"
                    value={bulkCfValues[cf.id] || ''}
                    onChange={e => setBulkCfValues(prev => ({ ...prev, [cf.id]: e.target.value || undefined }))}
                  />
                )}
              </div>
            ))}
            {allContractCFs.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">Nessun campo personalizzato configurato per i contratti.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Annulla</Button>
            <Button onClick={handleBulkEdit} disabled={bulkSaving || allContractCFs.length === 0}>
              {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Applica a {selectedContracts.size} contratt{selectedContracts.size === 1 ? 'o' : 'i'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* PM Snapshot Edit Dialog */}
      <Dialog open={pmSnapshotDialog.open} onOpenChange={(open) => {
        if (!open) {
          setPmSnapshotDialog({ open: false, contractId: null, contractProductId: null, currentPmIds: [], productName: '' });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Modifica Project Manager
            </DialogTitle>
            <DialogDescription>
              Modifica i Project Manager associati a &quot;{pmSnapshotDialog.productName}&quot; al momento dell&apos;esito.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Seleziona Project Manager</Label>
              <div className="border rounded-md max-h-60 overflow-y-auto">
                {pmUsers.map((pm) => (
                  <div
                    key={pm.id}
                    className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                      pmSnapshotDialog.currentPmIds.includes(pm.id) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => togglePmInSnapshot(pm.id)}
                  >
                    <Checkbox
                      checked={pmSnapshotDialog.currentPmIds.includes(pm.id)}
                      onCheckedChange={() => togglePmInSnapshot(pm.id)}
                    />
                    <span>{pm.name}</span>
                  </div>
                ))}
                {pmUsers.length === 0 && (
                  <p className="p-3 text-gray-500 text-sm">Nessun Project Manager disponibile</p>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {pmSnapshotDialog.currentPmIds.length} PM selezionati
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPmSnapshotDialog({ open: false, contractId: null, contractProductId: null, currentPmIds: [], productName: '' })}>
              Annulla
            </Button>
            <Button onClick={handlePmSnapshotSave}>
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Export CSV */}
      <Dialog open={exportDialogOpen} onOpenChange={(open) => !exporting && setExportDialogOpen(open)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="export-csv-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-orange-600" />
              Esporta CSV — Contratti
            </DialogTitle>
            <DialogDescription>
              Esporta <b>{dateFilteredContracts.length}</b> {dateFilteredContracts.length === 1 ? 'contratto' : 'contratti'} filtrati. Seleziona i campi da includere.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {[
              { title: 'Contratto — campi standard', groupKey: 'contract_std' },
              { title: 'Contratto — campi personalizzati', groupKey: 'contract_custom' },
              { title: 'Cliente — campi standard', groupKey: 'client_std' },
              { title: 'Cliente — campi personalizzati', groupKey: 'client_custom' },
            ].map(({ title, groupKey }) => {
              const items = exportFields[groupKey] || [];
              if (items.length === 0) return null;
              const groupKeys = items.map(i => i.key);
              const allSelected = groupKeys.every(k => selectedExportKeys.includes(k));
              const someSelected = groupKeys.some(k => selectedExportKeys.includes(k));
              return (
                <div key={groupKey} className="border rounded-lg p-3" data-testid={`export-group-${groupKey}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm text-gray-800">{title}</h4>
                    <button
                      type="button"
                      onClick={() => toggleExportGroup(groupKeys, allSelected)}
                      className="text-xs text-orange-600 hover:underline"
                      data-testid={`toggle-group-${groupKey}`}
                    >
                      {allSelected ? 'Deseleziona tutti' : someSelected ? 'Seleziona tutti' : 'Seleziona tutti'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {items.map(item => (
                      <label key={item.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-2 py-1">
                        <Checkbox
                          checked={selectedExportKeys.includes(item.key)}
                          onCheckedChange={() => toggleExportKey(item.key)}
                          data-testid={`export-field-${item.key}`}
                        />
                        <span className="truncate" title={item.label}>{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <div className="flex items-center gap-2 mr-auto text-xs text-gray-500">
              <b>{selectedExportKeys.length}</b> campi selezionati
            </div>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)} disabled={exporting}>
              Annulla
            </Button>
            <Button onClick={runExport} disabled={exporting || selectedExportKeys.length === 0} data-testid="export-csv-confirm">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Esporta CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
